/**
 * High neck blouse designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_blouse_designs`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const highNeckBlouseDesignsPack: ToilePack = {
    slug: 'high-neck-blouse-designs',
    category: 'fashion',
    name: 'High neck blouse designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'collar', label: 'Shirt collar', subject: 'a high neck blouse with a shirt collar and front buttons'},
        {key: 'mandarin', label: 'Mandarin', subject: 'a mandarin collar blouse in brocade'},
        {key: 'choker', label: 'Choker neck', subject: 'a high choker neck blouse with a cut-out below'},
    ],
    styles: [
        {key: 'tailor', label: "Tailor's shop", style: "photographed in an Indian tailor's shop with bolts of fabric and a measuring tape soft and out of focus behind, warm daylight from a window camera-left, gentle shadow on the far side, a balance of two-thirds light to one-third shade", palette: 'ivory with gold', framing: 'The dress form centered from waist to neck with room around it'},
        {key: 'boutique', label: 'Boutique', style: 'photographed in a bright boutique against a plain pale plaster wall, soft even daylight near-frontal, a faint shadow on the wall, an airy balance of mostly light with little shade', palette: 'black with silver', framing: 'The dress form centered from waist to neck with a clear margin of wall on each side'},
        {key: 'closeup', label: 'Close-up', style: 'a close view in daylight raking from camera-right, every thread, bead and seam crisp, a balance of deep shade in the folds and bright highlights on the work', palette: 'emerald green with antique gold', framing: 'The blouse filling the frame, the dress form cropped at the waist'},
    ],
    directions: ['full sleeves', 'silk saree', 'for winter'],
    directionHint: 'try: full sleeves',
    seo: {
        title: 'High Neck Blouse Designs: Collar, Mandarin, Choker',
        metaDescription: 'Nine high neck blouses: a shirt collar, a mandarin and a choker neck, each in three views.',
        h1: 'High neck blouse designs: shirt collar, mandarin, choker',
        tagline: "Three high necks, each in a tailor's shop, a bright boutique, and as a close-up of the collar.",
        intro: "Saree blouses with a high neck in three forms: a shirt collar with front buttons, a mandarin collar in brocade, and a high choker neck with a cut-out below. Each sits on a dress form in a tailor's shop, a bright boutique or as a close-up of the collar. Add a direction, such as 'full sleeves' or 'for winter', to ask for a change; the picture is a request and may differ.",
        useCases: [
            'Showing a tailor the collar you want',
            'A high neck blouse for a winter wedding',
            'Choosing between a shirt collar and a mandarin',
            'A choker neck for a formal event',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three high necks: a shirt collar with front buttons, a mandarin collar and a choker neck with a cut-out.'},
            {q: 'What are the three views?', a: "A tailor's shop, a bright boutique and a close-up of the collar."},
            {q: 'Can I change the sleeves or the season?', a: "Yes. Add a direction such as 'full sleeves' or 'for winter'. It is a request, so the result may not match the nine."},
            {q: 'Can I print these?', a: 'They are portrait-shaped pictures, so they suit a portrait page. Check the size you need before you print.'},
        ],
    },
}
